import { Button } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";
import { Badge } from "../ui/badge";
import { Sun, Moon, Coffee, Trash, Plus } from "lucide-react";
import { cn } from "../../lib/utils";

const COLORI_REPARTO = {
  'cucina': { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-400', dot: 'bg-orange-500', label: 'Cucina' },
  'sala': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-400', dot: 'bg-blue-500', label: 'Sala' },
  'laboratorio': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-400', dot: 'bg-purple-500', label: 'Lab' },
  'altro': { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-400', dot: 'bg-gray-500', label: 'Altro' }
};

const getFasciaIcon = (fascia) => {
  const ora = parseInt(fascia.ora_inizio?.split(":")[0] || 12);
  if (ora < 12) return <Sun className="h-4 w-4 text-amber-500" />;
  if (ora < 18) return <Coffee className="h-4 w-4 text-orange-500" />;
  return <Moon className="h-4 w-4 text-indigo-500" />;
};

const getColoreReparto = (reparto) => COLORI_REPARTO[reparto] || COLORI_REPARTO['altro'];

export const CalendarioTurni = ({ giorni, fasce, turni, operatori, assenzeSettimana = [], oggi, settimanaFine, onOpenSlot, onCancellaTurni, getTurniPerSlot, onAddCustomTurno, onDeleteTurno }) => (
  <>
    {settimanaFine >= oggi && turni.length > 0 && (
      <div className="px-4 pt-3 pb-1">
        <Button variant="outline" size="sm" onClick={onCancellaTurni} className="w-full border-red-200 text-red-500 hover:bg-red-50 hover:border-red-300 text-xs" data-testid="cancella-turni-btn">
          <Trash className="h-3.5 w-3.5 mr-1.5" />
          Elimina turni della settimana {giorni[0]?.giorno} {giorni[0]?.mese} - {giorni[6]?.giorno} {giorni[6]?.mese}
        </Button>
      </div>
    )}

    <div className="p-4 space-y-4">
      {giorni.map((giorno, giornoIdx) => {
        const fasceAttive = fasce.filter(f => !f.giorni_attivi || f.giorni_attivi.includes(giornoIdx));
        const isPassato = giorno.data < oggi;

        return (
          <Card key={giorno.data} className={cn("overflow-hidden", isPassato && "opacity-70")}>
            <CardHeader className={cn("py-2 px-3", isPassato ? "bg-muted/60" : "bg-muted/30")}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{giorno.nome}</span>
                  {isPassato && <Badge variant="secondary" className="text-[10px] bg-muted text-muted-foreground">Passato</Badge>}
                </div>
                <span className="text-sm text-muted-foreground">{giorno.giorno} {giorno.mese}</span>
              </div>
            </CardHeader>
            <CardContent className="p-2 space-y-2">
              {fasceAttive.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-4">
                  <p className="text-sm text-muted-foreground">Nessuna fascia turno per questo giorno</p>
                  {!isPassato && onAddCustomTurno && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-dashed border-slate-300 text-slate-500 hover:border-indigo-400 hover:text-indigo-600 text-xs"
                      onClick={() => onAddCustomTurno(giorno.data)}
                      data-testid={`add-custom-turno-${giorno.data}`}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1.5" />
                      Aggiungi turno
                    </Button>
                  )}
                </div>
              ) : (
                fasceAttive.map(fascia => {
                  const turniSlot = getTurniPerSlot(giorno.data, fascia.id);
                  const assenzeGiorno = assenzeSettimana.filter(a => a.data === giorno.data);
                  const assenzeUniche = {};
                  assenzeGiorno.forEach(a => { if (!assenzeUniche[a.utente_id]) assenzeUniche[a.utente_id] = a; });
                  const assenzeList = Object.values(assenzeUniche);

                  const turniPerReparto = {};
                  turniSlot.forEach(t => {
                    const op = operatori.find(o => o.id === t.utente_id);
                    const rep = op?.ruolo || t.reparto || 'altro';
                    if (!turniPerReparto[rep]) turniPerReparto[rep] = [];
                    turniPerReparto[rep].push({ ...t, operatore: op });
                  });

                  return (
                    <button key={fascia.id} onClick={() => !isPassato && onOpenSlot(giorno.data, fascia)} disabled={isPassato}
                      className={cn("w-full p-3 rounded-lg border text-left transition-colors", isPassato ? "bg-muted/30 cursor-not-allowed" : "bg-card hover:bg-muted/50 cursor-pointer")}
                      data-testid={`slot-${giorno.data}-${fascia.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {getFasciaIcon(fascia)}
                        <span className="text-sm font-medium">{fascia.nome}</span>
                        <span className="text-xs text-muted-foreground">{fascia.ora_inizio} - {fascia.ora_fine}</span>
                        <Badge variant="outline" className="ml-auto text-xs">{turniSlot.length} operatori</Badge>
                      </div>
                      {turniSlot.length > 0 ? (
                        <div className="space-y-1">
                          {Object.entries(turniPerReparto).map(([reparto, turniRep]) => {
                            const colore = getColoreReparto(reparto);
                            return (
                              <div key={reparto} className="flex flex-wrap items-center gap-1">
                                <span className={cn("text-xs font-medium px-1.5 py-0.5 rounded", colore.bg, colore.text)}>{colore.label}:</span>
                                {turniRep.map(t => (
                                  <Badge key={t.id} variant="secondary" className={cn("text-xs", colore.bg, colore.text, "border", colore.border)}>
                                    {t.utente_nome?.split(' ')[0] || t.utente_nome}
                                  </Badge>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">Nessuno assegnato</p>
                      )}
                      {assenzeList.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-border/50 space-y-0.5">
                          {assenzeList.map((a, i) => (
                            <div key={i} className="flex items-center gap-1">
                              <span className={cn("text-[10px] font-medium px-1.5 py-0.5 rounded-full",
                                a.tipo === "malattia" ? "bg-red-100 text-red-600" : a.tipo === "permesso" ? "bg-amber-100 text-amber-600" : "bg-blue-100 text-blue-600"
                              )}>
                                {(a.utente_nome || "").split(' ')[0]} - {a.tipo === "malattia" ? "Malattia" : a.tipo === "permesso" ? "Permesso" : "Ferie"}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
    {/* Turni orfani: turni nel DB il cui giorno non corrisponde alla fascia attiva */}
    {(() => {
      const orfani = [];
      giorni.forEach((giorno, idx) => {
        const turniGiorno = turni.filter(t => t.data === giorno.data);
        turniGiorno.forEach(t => {
          if (!t.fascia_turno_id) return; // turni custom senza fascia: ok
          const fascia = fasce.find(f => f.id === t.fascia_turno_id);
          if (!fascia) { orfani.push({ turno: t, giorno, motivo: "Fascia eliminata" }); return; }
          if (fascia.giorni_attivi && !fascia.giorni_attivi.includes(idx)) {
            orfani.push({ turno: t, giorno, fascia, motivo: `"${fascia.nome}" non attiva il ${giorno.nome}` });
          }
        });
      });
      if (!orfani.length) return null;
      return (
        <div className="px-4 pb-6">
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-3">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-2 flex items-center gap-1.5">
              <span>⚠️</span> Turni anomali rilevati ({orfani.length}) — visibili agli operatori ma non in questa griglia
            </p>
            <div className="space-y-1.5">
              {orfani.map(({ turno, giorno, motivo }) => {
                const op = operatori.find(o => o.id === turno.utente_id);
                return (
                  <div key={turno.id} className="flex items-center justify-between bg-white dark:bg-card rounded-md px-3 py-2 border border-amber-100 dark:border-amber-800 text-xs">
                    <div>
                      <span className="font-medium">{op?.nome || turno.utente_nome || turno.utente_id}</span>
                      <span className="text-muted-foreground ml-2">· {giorno.nome} {giorno.giorno}/{giorno.mese} · {motivo}</span>
                    </div>
                    {onDeleteTurno && (
                      <button onClick={() => onDeleteTurno(turno.id)} className="ml-2 text-red-500 hover:text-red-700 text-xs font-medium hover:underline" data-testid={`delete-orfano-${turno.id}`}>
                        Elimina
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    })()}
  </>
);
